import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X, Loader2, AlertTriangle } from 'lucide-react';
import {
  searchService,
  type OwnerSearchResult,
  type OwnerSearchType,
} from '@services/search.service';
import { cn } from '@lib/utils';

// ── Owner picker ───────────────────────────────────────────────────────────
// Operators know names, not UUIDs. This is the one control that turns "Jane
// Wanjiku" into an owner id — and, just as importantly, shows enough detail
// (phone, email, roles, parent business) that the operator can SEE they picked
// the right Jane before money moves against her wallet.

const MIN_QUERY = 2;
const DEBOUNCE_MS = 250;

const inputClass =
  'w-full rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-3 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors';

const labelClass =
  'block text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1.5';

export const ownerTypeOptions: { value: OwnerSearchType; label: string }[] = [
  { value: 'user', label: 'User' },
  { value: 'business', label: 'Business' },
  { value: 'branch', label: 'Branch' },
];

const placeholderFor: Record<OwnerSearchType, string> = {
  user: 'Search by name, phone or email...',
  business: 'Search by business name, phone or email...',
  branch: 'Search by branch or business name...',
};

/** Role chips: the only thing separating two people with the same name. */
function roleChips(owner: OwnerSearchResult): string[] {
  if (owner.ownerType !== 'user') return [];
  const chips: string[] = [];
  if (owner.isCustomer) chips.push('Customer');
  if (owner.isRider) chips.push('Rider');
  if (owner.isBusinessUser) chips.push('Merchant');
  if (owner.isChekitStaff) chips.push('Staff');
  return chips;
}

/** The one-line disambiguator shown under the name, in both the dropdown and
 *  the selected chip. Never empty — falls back to a short id. */
export function ownerDetailLine(owner: OwnerSearchResult): string {
  const parts: string[] = [];
  if (owner.ownerType === 'branch') {
    if (owner.businessName) parts.push(owner.businessName);
    if (owner.city) parts.push(owner.city);
    if (owner.phone) parts.push(owner.phone);
  } else {
    if (owner.phone) parts.push(owner.phone);
    if (owner.email) parts.push(owner.email);
    if (owner.ownerType === 'business' && owner.ownerName) {
      parts.push(`Owner: ${owner.ownerName}`);
    }
  }
  if (parts.length === 0) parts.push(`${owner.ownerId.slice(0, 8)}…`);
  return parts.join(' · ');
}

interface OwnerPickerProps {
  ownerType: OwnerSearchType;
  value: OwnerSearchResult | null;
  onChange: (owner: OwnerSearchResult | null) => void;
  label?: string;
  placeholder?: string;
  /** Rendered under the field — use it to explain what the pick will do. */
  hint?: string;
  autoFocus?: boolean;
}

export const OwnerPicker = ({
  ownerType,
  value,
  onChange,
  label,
  placeholder,
  hint,
  autoFocus,
}: OwnerPickerProps) => {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const reactId = useId();
  const inputId = `owner-picker-${reactId}`;
  const listId = `owner-picker-list-${reactId}`;

  // A selection made under one owner type is meaningless under another.
  useEffect(() => {
    if (value && value.ownerType !== ownerType) onChange(null);
  }, [ownerType, value, onChange]);

  // Debounce: one request per pause, not one per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [term]);

  const enabled = open && debounced.length >= MIN_QUERY;

  const { data, isFetching, error } = useQuery({
    queryKey: ['owner-search', ownerType, debounced],
    queryFn: () => searchService.searchOwners({ type: ownerType, q: debounced }),
    enabled,
    retry: false,
    staleTime: 30_000,
  });

  const results = useMemo(() => (enabled ? (data ?? []) : []), [enabled, data]);

  useEffect(() => setHighlight(0), [results]);

  // Click outside closes the dropdown without clearing what was typed.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const select = (owner: OwnerSearchResult) => {
    onChange(owner);
    setTerm('');
    setDebounced('');
    setOpen(false);
  };

  const clear = () => {
    onChange(null);
    setTerm('');
    setDebounced('');
    setOpen(false);
    // Put the operator straight back into searching.
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => (results.length === 0 ? 0 : (h + 1) % results.length));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (results.length === 0 ? 0 : (h - 1 + results.length) % results.length));
      return;
    }
    if (e.key === 'Enter') {
      const picked = results[highlight];
      if (picked) {
        e.preventDefault();
        select(picked);
      }
    }
  };

  // ── Selected: a chip carrying enough detail to spot a wrong pick ────────
  if (value) {
    const chips = roleChips(value);
    return (
      <div>
        {label && <span className={labelClass}>{label}</span>}
        <div className="flex items-start justify-between gap-3 rounded-2xl border border-[var(--border-strong)] bg-[var(--bg-elevated)] px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-[14px] font-semibold text-[var(--text-primary)]">
              {value.label}
            </div>
            <div className="truncate text-[12px] text-[var(--text-muted)]">
              {ownerDetailLine(value)}
            </div>
            {chips.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {chips.map((c) => (
                  <span
                    key={c}
                    className="rounded-full bg-[var(--state-success-bg)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--state-success)]"
                  >
                    {c}
                  </span>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={clear}
            aria-label="Change selection"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[var(--border-default)] px-3 py-1.5 text-[12px] font-medium text-[var(--text-muted)] transition-colors hover:border-[var(--border-strong)] hover:text-[var(--text-primary)]"
          >
            <X className="h-3.5 w-3.5" strokeWidth={1.5} />
            Change
          </button>
        </div>
        {hint && <p className="mt-1.5 text-[12px] text-[var(--text-muted)]">{hint}</p>}
      </div>
    );
  }

  // ── Searching ───────────────────────────────────────────────────────────
  const showDropdown = open && term.trim().length > 0;
  const tooShort = debounced.length < MIN_QUERY;

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className={labelClass} htmlFor={inputId}>
          {label}
        </label>
      )}

      <div className="relative">
        <Search
          className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-muted)]"
          strokeWidth={1.5}
        />
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            showDropdown && results[highlight] ? `${listId}-${highlight}` : undefined
          }
          autoComplete="off"
          autoFocus={autoFocus}
          value={term}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder ?? placeholderFor[ownerType]}
          className={cn(inputClass, 'pl-10 pr-10')}
        />
        {isFetching && (
          <Loader2
            className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-[var(--text-muted)]"
            strokeWidth={1.5}
          />
        )}
      </div>

      {hint && !showDropdown && (
        <p className="mt-1.5 text-[12px] text-[var(--text-muted)]">{hint}</p>
      )}

      {showDropdown && (
        <div className="absolute left-0 right-0 top-full z-30 mt-2 max-h-[320px] overflow-y-auto rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-soft">
          {tooShort && (
            <p className="px-4 py-4 text-[13px] text-[var(--text-muted)]">
              Keep typing — at least {MIN_QUERY} characters.
            </p>
          )}

          {!tooShort && !!error && (
            <p className="flex items-center gap-2 px-4 py-4 text-[13px] text-[var(--state-danger)]">
              <AlertTriangle className="h-4 w-4 shrink-0" strokeWidth={1.5} />
              Search failed. Try again.
            </p>
          )}

          {!tooShort && !error && isFetching && results.length === 0 && (
            <p className="px-4 py-4 text-[13px] text-[var(--text-muted)]">Searching...</p>
          )}

          {!tooShort && !error && !isFetching && results.length === 0 && (
            <p className="px-4 py-4 text-[13px] text-[var(--text-muted)]">
              Nothing matches “{debounced}”.
            </p>
          )}

          {results.length > 0 && (
            <ul id={listId} role="listbox" className="divide-y divide-[var(--border-subtle)]">
              {results.map((owner, i) => {
                const chips = roleChips(owner);
                return (
                  <li
                    key={owner.ownerId}
                    id={`${listId}-${i}`}
                    role="option"
                    aria-selected={i === highlight}
                  >
                    <button
                      type="button"
                      onMouseEnter={() => setHighlight(i)}
                      onClick={() => select(owner)}
                      className={cn(
                        'block w-full px-4 py-3 text-left transition-colors',
                        i === highlight ? 'bg-[var(--bg-elevated)]' : 'hover:bg-[var(--bg-base)]',
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-[14px] font-medium text-[var(--text-primary)]">
                            {owner.label}
                          </div>
                          <div className="truncate text-[12px] text-[var(--text-muted)]">
                            {ownerDetailLine(owner)}
                          </div>
                        </div>
                        {owner.status && (
                          <span className="shrink-0 rounded-full bg-[var(--bg-base)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                            {owner.status.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                      {chips.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {chips.map((c) => (
                            <span
                              key={c}
                              className="rounded-full bg-[var(--state-success-bg)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--state-success)]"
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

// ── Owner type toggle ──────────────────────────────────────────────────────
// A segmented control rather than a <select>: three options that change what
// the picker beside it searches should be visible without opening anything.

interface OwnerTypeToggleProps {
  value: OwnerSearchType;
  onChange: (value: OwnerSearchType) => void;
  label?: string;
}

export const OwnerTypeToggle = ({ value, onChange, label }: OwnerTypeToggleProps) => (
  <div>
    {label && <span className={labelClass}>{label}</span>}
    <div className="inline-flex flex-wrap gap-2" role="group" aria-label={label ?? 'Owner type'}>
      {ownerTypeOptions.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded-full border px-4 py-2 text-[13px] font-medium transition-colors',
            value === o.value
              ? 'border-[var(--border-strong)] bg-[var(--bg-elevated)] text-[var(--text-primary)]'
              : 'border-[var(--border-default)] text-[var(--text-muted)] hover:border-[var(--border-strong)] hover:text-[var(--text-primary)]',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  </div>
);
